import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const EMPTY = { name: "", phone: "+91 ", issue_description: "", preferred_time: "" };

export default function QuickRequestForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please add your name"); return; }
    if (form.phone.replace(/\D/g, "").length < 10) { toast.error("Please add your phone number"); return; }
    if (form.issue_description.trim().length < 3) { toast.error("Please describe the issue"); return; }
    setLoading(true);
    try {
      await api.post("/quick-requests", form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quick-request" data-testid="quick-request-section">
      <div className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Quick <span className="text-brand">request</span>
          </h2>
          <p className="text-slate-400 mt-3">No sign-up needed. Tell us what's wrong and we'll reach out on WhatsApp.</p>
        </div>
        <div className="soft-card p-6 sm:p-8">
          {done ? (
            <div data-testid="quick-request-success" className="text-center py-6">
              <CheckCircle2 className="w-10 h-10 text-brand mx-auto" />
              <p className="mt-3 text-lg font-semibold text-slate-900">Thanks! We'll WhatsApp you within 2 hours.</p>
              <Button data-testid="quick-request-again-btn" variant="outline" onClick={() => setDone(false)} className="mt-5 rounded-full border border-slate-200 text-slate-900">
                Send another request
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="quick-request-form">
              <div>
                <Label htmlFor="qr-name" className="font-semibold text-slate-900">Name *</Label>
                <Input id="qr-name" data-testid="quick-request-name-input" required value={form.name} onChange={set("name")} className="mt-1 border border-slate-200 h-11" />
              </div>
              <div>
                <Label htmlFor="qr-phone" className="font-semibold text-slate-900">Phone number *</Label>
                <Input id="qr-phone" data-testid="quick-request-phone-input" type="tel" inputMode="tel" required value={form.phone} onChange={set("phone")} className="mt-1 border border-slate-200 h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="qr-issue" className="font-semibold text-slate-900">Issue description *</Label>
                <Textarea id="qr-issue" data-testid="quick-request-issue-input" required rows={4} value={form.issue_description} onChange={set("issue_description")} placeholder="e.g. Laptop won't turn on after an update" className="mt-1 border border-slate-200" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="qr-time" className="font-semibold text-slate-900">Preferred date/time (optional)</Label>
                <Input id="qr-time" data-testid="quick-request-time-input" type="datetime-local" value={form.preferred_time} onChange={set("preferred_time")} className="mt-1 border border-slate-200 h-11" />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading} data-testid="quick-request-submit-btn" className="w-full rounded-full bg-brand hover:bg-brand-dark text-white font-bold h-12 shadow-btn">
                  {loading ? "Sending…" : "Send request"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
